import { Platform } from 'react-native';

import type { HitSlop } from '@/util/accessibleTouchTarget';

type WebPressProps = {
  onClick: () => void;
  tabIndex: 0;
  onKeyDown: (event: { key: string; preventDefault: () => void }) => void;
};

/**
 * Returns platform-appropriate press props for elements that must handle
 * tap/click inline inside a `<Text>` tree.
 *
 * On web, RN-Web does not propagate `onPress` on inline `<Text>` nodes
 * (the prop leaks to the DOM as an unknown attribute). Bind `onClick`
 * directly to the underlying DOM element instead. The element is a plain
 * span, which a keyboard can neither reach nor activate, so it also gets a
 * tab stop and fires on Enter or Space, as a button would.
 *
 * On native, bind `onPress` plus an optional `hitSlop` to expand the
 * touch target toward WCAG 2.2 SC 2.5.8 minimums.
 *
 * Usage:
 *   <Text {...getPressProps(onPress, getAccessibleHitSlop(24))} />
 */
export function getPressProps(
  onPress: () => void,
  hitSlop?: HitSlop
): WebPressProps | { onPress: () => void; hitSlop?: HitSlop } {
  if (Platform.OS === 'web') {
    return {
      onClick: onPress,
      tabIndex: 0,
      onKeyDown: (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();

          onPress();
        }
      },
    };
  }

  return hitSlop !== undefined ? { onPress, hitSlop } : { onPress };
}
